/* Import fonts for distinctive typography.
 *
 * THUB-293: this @import MUST stay above the @tailwind directives. CSS requires @import to
 * precede all other statements; CRA's postcss silently hoisted it to the top of the emitted
 * stylesheet, so it worked despite being third. Vite instead warns and DROPS the rule, which
 * would have shipped a build with Sora/DM Sans/JetBrains Mono missing and every heading
 * falling back to system-ui -- a visible regression that a green build does not reveal.
 */
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Sora:wght@100..800&family=JetBrains+Mono:wght@400;500;600&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  /* Set root font and smooth scrolling */
  html {
    @apply scroll-smooth;
    font-feature-settings: "cv02", "cv03", "cv04", "cv11";
  }

  body {
    @apply font-body text-base text-neutral-800 bg-neutral-150 antialiased;
    @apply dark:text-neutral-100 dark:bg-neutral-900;
  }

  /* Headings use display font */
  h1, h2, h3, h4, h5, h6 {
    @apply font-display font-semibold tracking-tight text-neutral-900;
    @apply dark:text-white;
  }

  h1 { @apply text-4xl md:text-5xl; }
  h2 { @apply text-3xl md:text-4xl; }
  h3 { @apply text-2xl md:text-3xl; }
  h4 { @apply text-xl md:text-2xl; }
  h5 { @apply text-lg md:text-xl; }
  h6 { @apply text-base md:text-lg; }

  /* Focus visible for accessibility */
  *:focus-visible {
    @apply outline-none ring-2 ring-primary-500 ring-offset-2;
    @apply dark:ring-offset-neutral-900;
  }

  /* Selection color */
  ::selection {
    @apply bg-primary-500/20 text-primary-900;
    @apply dark:bg-primary-400/30 dark:text-primary-100;
  }
}

@layer components {
  /* Button base styles */
  .btn {
    @apply inline-flex items-center justify-center gap-2;
    @apply px-4 py-2.5 rounded-lg font-body font-medium text-sm;
    @apply transition-all duration-200 ease-smooth;
    @apply min-h-touch min-w-touch;
    @apply focus-visible:ring-2 focus-visible:ring-offset-2;
    @apply disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none;
  }

  .btn-primary {
    @apply btn bg-primary-600 text-white;
    @apply hover:bg-primary-700 active:bg-primary-800;
    @apply focus-visible:ring-primary-500;
    @apply shadow-sm hover:shadow-primary;
  }

  .btn-secondary {
    @apply btn bg-neutral-100 text-neutral-700 border border-neutral-300;
    @apply hover:bg-neutral-200 active:bg-neutral-300;
    @apply focus-visible:ring-neutral-400;
    @apply dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700;
    @apply dark:hover:bg-neutral-700 dark:active:bg-neutral-600;
  }

  .btn-danger {
    @apply btn bg-danger-500 text-white;
    @apply hover:bg-danger-600 active:bg-danger-700;
    @apply focus-visible:ring-danger-500;
    @apply shadow-sm hover:shadow-danger;
  }

  .btn-ghost {
    @apply btn bg-transparent text-neutral-600;
    @apply hover:bg-neutral-100 active:bg-neutral-200;
    @apply focus-visible:ring-neutral-400;
    @apply dark:text-neutral-300 dark:hover:bg-neutral-800 dark:active:bg-neutral-700;
  }

  .btn-success {
    @apply btn bg-success-500 text-white;
    @apply hover:bg-success-600 active:bg-success-700;
    @apply focus-visible:ring-success-500;
    @apply shadow-sm hover:shadow-success;
  }

  /* Card component */
  .card {
    @apply bg-white rounded-xl shadow-sm border border-neutral-200;
    @apply dark:bg-neutral-850 dark:border-neutral-700 dark:shadow-dark;
  }

  .card-header {
    @apply px-5 py-4 border-b border-neutral-200;
    @apply dark:border-neutral-700;
  }

  .card-body {
    @apply px-5 py-4;
  }

  .card-footer {
    @apply px-5 py-4 border-t border-neutral-200 bg-neutral-50;
    @apply dark:border-neutral-700 dark:bg-neutral-900/50;
  }

  /* Form inputs */
  .input {
    @apply w-full px-3.5 py-2.5 rounded-lg font-body text-sm;
    @apply bg-white border border-neutral-300 text-neutral-900;
    @apply placeholder:text-neutral-400;
    @apply transition-all duration-200 ease-smooth;
    @apply focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20;
    @apply dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-100;
    @apply dark:placeholder:text-neutral-500;
    @apply dark:focus:border-primary-400 dark:focus:ring-primary-400/20;
    @apply disabled:bg-neutral-100 disabled:text-neutral-500 disabled:cursor-not-allowed;
    @apply dark:disabled:bg-neutral-900 dark:disabled:text-neutral-600;
  }

  .input-error {
    @apply border-danger-500 focus:border-danger-500 focus:ring-danger-500/20;
    @apply dark:border-danger-400 dark:focus:border-danger-400 dark:focus:ring-danger-400/20;
  }

  .label {
    @apply block text-sm font-medium text-neutral-700 mb-1.5;
    @apply dark:text-neutral-300;
  }

  /* Status badges */
  .badge {
    @apply inline-flex items-center gap-1 px-2.5 py-1 rounded-full;
    @apply text-xs font-medium;
  }

  .badge-success {
    @apply badge bg-success-100 text-success-700;
    @apply dark:bg-success-900/30 dark:text-success-400;
  }

  .badge-danger {
    @apply badge bg-danger-100 text-danger-700;
    @apply dark:bg-danger-900/30 dark:text-danger-400;
  }

  .badge-warning {
    @apply badge bg-warning-100 text-warning-700;
    @apply dark:bg-warning-900/30 dark:text-warning-400;
  }

  .badge-info {
    @apply badge bg-primary-100 text-primary-700;
    @apply dark:bg-primary-900/30 dark:text-primary-400;
  }

  .badge-neutral {
    @apply badge bg-neutral-100 text-neutral-600;
    @apply dark:bg-neutral-800 dark:text-neutral-400;
  }

  /* Table styles */
  .table {
    @apply w-full text-sm;
  }

  .table-header {
    @apply bg-neutral-50 border-b border-neutral-200;
    @apply dark:bg-neutral-800/50 dark:border-neutral-700;
  }

  .table-header th {
    @apply px-4 py-3 text-left font-semibold text-neutral-600 uppercase tracking-wider text-xs;
    @apply dark:text-neutral-400;
  }

  .table-row {
    @apply border-b border-neutral-100 transition-colors duration-150;
    @apply hover:bg-neutral-50;
    @apply dark:border-neutral-800 dark:hover:bg-neutral-800/50;
  }

  .table-row td {
    @apply px-4 py-3.5 text-neutral-700;
    @apply dark:text-neutral-300;
  }

  /* Skeleton loading animation */
  .skeleton {
    @apply animate-shimmer bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200;
    @apply bg-[length:200%_100%] rounded;
    @apply dark:from-neutral-800 dark:via-neutral-700 dark:to-neutral-800;
  }

  /* Toast notifications */
  .toast {
    @apply flex items-start gap-3 p-4 rounded-xl shadow-lg;
    @apply bg-white border border-neutral-200;
    @apply dark:bg-neutral-850 dark:border-neutral-700;
    @apply animate-slide-in-right;
  }

  .toast-success {
    @apply toast border-l-4 border-l-success-500;
  }

  .toast-error {
    @apply toast border-l-4 border-l-danger-500;
  }

  .toast-warning {
    @apply toast border-l-4 border-l-warning-500;
  }

  .toast-info {
    @apply toast border-l-4 border-l-primary-500;
  }

  /* Modal backdrop */
  .modal-backdrop {
    @apply fixed inset-0 bg-black/50 backdrop-blur-xs z-40;
    @apply animate-fade-in;
  }

  .modal-content {
    @apply bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4;
    @apply dark:bg-neutral-850;
    @apply animate-scale-in;
  }

  /* Connection status indicator */
  .connection-indicator {
    @apply flex items-center gap-2 text-xs font-medium;
  }

  .connection-dot {
    @apply w-2 h-2 rounded-full;
  }

  .connection-dot-connected {
    @apply connection-dot bg-success-500 animate-pulse-soft;
  }

  .connection-dot-disconnected {
    @apply connection-dot bg-danger-500;
  }

  .connection-dot-reconnecting {
    @apply connection-dot bg-warning-500 animate-pulse;
  }

  /* Scrollbar styling */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: theme('colors.neutral.300') transparent;
  }

  .scrollbar-thin::-webkit-scrollbar {
    @apply w-2 h-2;
  }

  .scrollbar-thin::-webkit-scrollbar-track {
    @apply bg-transparent;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb {
    @apply bg-neutral-300 rounded-full;
    @apply dark:bg-neutral-600;
  }

  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    @apply bg-neutral-400;
    @apply dark:bg-neutral-500;
  }
}

@layer utilities {
  /* Text gradient utility */
  .text-gradient {
    @apply bg-clip-text text-transparent bg-cgic-gradient;
  }

  /* Glass effect */
  .glass {
    @apply bg-white/80 backdrop-blur-md border border-white/20;
    @apply dark:bg-neutral-900/80 dark:border-neutral-700/50;
  }

  /* Truncate with fade */
  .truncate-fade {
    @apply relative overflow-hidden;
    mask-image: linear-gradient(to right, black 80%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, black 80%, transparent 100%);
  }

  /* Hide scrollbar but keep functionality */
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
}
